import { useCallback, useContext, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Progress,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { EditorHTML } from '@duke-hq/ui/components/DukeTiptapEditor/EditorHTML';
import { AssessmentFormRenderer } from '@duke-hq/ui/components/AssessmentForm/AssessmentFormRenderer';
import {
  FileUploadContext,
  FileUploadRoot,
  FileUploadDropzone,
  FileUploadList,
} from '@duke-hq/ui/components/FileUpload/FileUpload';
import { isMediaRecordingSupported } from '@duke-hq/ui/components/MediaRecorder/MediaRecorder';
import { RecordResponse } from '@duke-hq/ui/components/MediaRecorder/RecordResponse';
import { MAX_FILE_UPLOAD_SIZE_MB } from '@duke-hq/svc-files-lib/schemas';
import { useFlag } from '@duke-hq/flags/react';
import { SlideKind, type UnitAssessmentRecordingKind } from '@duke-hq/svc-api-db';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ChevronLeft, ChevronRight, Upload } from 'lucide-react';
import {
  SingleChoiceAnswer,
  SingleChoiceAnswerCard,
} from '../../Assessments/components/SingleChoiceAnswer';
import type { ReactNode } from 'react';
import type { PreviewVersion } from './PreviewModal';
import type { FormSection } from '@duke-hq/shared-constants/assessments';
import { FileUrlsProvider } from '@duke-hq/svc-files-ui/useTiptapFileSrcResolver';
import type { GetAdminUnitAssessmentSides } from '@duke-hq/svc-api/admin/trpc/routers/unitAssessment/types/getUnitAssessmentSlidesTypes';

type AssessmentPreviewProps = {
  unitId: string;
  lastPublishedVersionId: string | null;
  version: PreviewVersion;
  versionToggle?: ReactNode;
};

type AssessmentSlidesPreviewProps = {
  slides: GetAdminUnitAssessmentSides[];
};

type LocalAnswer = {
  selectedOptionId?: string;
  textInput?: string;
  formState?: Record<string, { textInputValue?: string; selectedOptions?: string[] }>;
};

const PREVIEW_MAX_FILES = 5;
const maxFileSizeGb = MAX_FILE_UPLOAD_SIZE_MB / 1024;

type FlatSlide = {
  id: string;
  title: string;
  kind: SlideKind;
  content?: unknown;
  questionContent?: unknown;
  hint?: unknown;
  options?: { id: string; answer: string; feedback?: string; isCorrect?: boolean }[];
  sections?: FormSection[];
  recordingKinds?: UnitAssessmentRecordingKind[];
};

function flattenSlides(slides: GetAdminUnitAssessmentSides[]): FlatSlide[] {
  const result: FlatSlide[] = [];

  for (const slide of slides) {
    if (slide.kind === SlideKind.QUESTION_GROUP && slide.questions) {
      for (const question of slide.questions) {
        result.push({
          id: question.id,
          title: slide.title,
          kind: slide.kind,
          questionContent: question.questionContent,
          options: question.options,
        });
      }
    } else {
      result.push({
        id: slide.id,
        title: slide.title,
        kind: slide.kind,
        content: 'content' in slide ? slide.content : undefined,
        questionContent: 'questionContent' in slide ? slide.questionContent : undefined,
        hint: 'hint' in slide ? slide.hint : undefined,
        sections: 'sections' in slide ? slide.sections : undefined,
        recordingKinds: 'recordingKinds' in slide ? slide.recordingKinds : undefined,
      });
    }
  }

  return result;
}

function PreviewSlideContent({ slide }: { slide: FlatSlide }) {
  if (slide.kind === SlideKind.CONTENT && slide.content) {
    return <EditorHTML content={slide.content} style={{ padding: 0 }} />;
  }

  if (
    (slide.kind === SlideKind.OPEN_QUESTION || slide.kind === SlideKind.FILE_UPLOAD_PROMPT) &&
    slide.questionContent
  ) {
    return <EditorHTML content={slide.questionContent} style={{ padding: 0 }} />;
  }

  return null;
}

function PreviewFormSlide({
  slide,
  answer,
  setAnswer,
}: {
  slide: FlatSlide;
  answer: LocalAnswer | undefined;
  setAnswer: (questionId: string, answer: Partial<LocalAnswer>) => void;
}) {
  const answerFormState = answer?.formState;
  const formState = useMemo(() => {
    const sections = slide.sections ?? [];
    const saved = answerFormState ?? {};
    return sections.reduce<Record<string, { textInputValue?: string; selectedOptions?: string[] }>>(
      (acc, section) => {
        for (const field of section.fields) {
          acc[field.id] = saved[field.id] ?? {};
        }
        return acc;
      },
      {},
    );
  }, [slide.sections, answerFormState]);

  return (
    <AssessmentFormRenderer
      slide={{ id: slide.id, sections: slide.sections ?? [] }}
      formSlideState={formState}
      onTextChange={({ fieldId, value }) => {
        setAnswer(slide.id, {
          formState: {
            ...formState,
            [fieldId]: { ...formState[fieldId], textInputValue: value },
          },
        });
      }}
      onTextBlur={async () => {}}
      onOptionChange={async ({ fieldId, value }) => {
        setAnswer(slide.id, {
          formState: {
            ...formState,
            [fieldId]: { ...formState[fieldId], selectedOptions: [value] },
          },
        });
      }}
    />
  );
}

// Takes stay in the preview's file list, like files picked here; nothing uploads.
function PreviewRecordResponse({
  recordingKinds,
}: {
  recordingKinds: UnitAssessmentRecordingKind[];
}) {
  const fileUpload = useContext(FileUploadContext);
  return (
    <RecordResponse
      recordingKinds={recordingKinds}
      onSave={fileUpload ? (file) => fileUpload.handleFileChange([file]) : undefined}
      fileCount={fileUpload?.files.length}
      maxFiles={fileUpload?.maxFiles}
    />
  );
}

function PreviewSlideQuestion({
  slide,
  answer,
  setAnswer,
}: {
  slide: FlatSlide;
  answer: LocalAnswer | undefined;
  setAnswer: (questionId: string, answer: Partial<LocalAnswer>) => void;
}) {
  const isMediaRecordingReleased = useFlag('ASSESSMENT_MEDIA_RECORDING_RELEASE') === true;

  if (slide.kind === SlideKind.QUESTION_GROUP && slide.options) {
    const selectedId = answer?.selectedOptionId;
    const selectedOption = slide.options.find((o) => o.id === selectedId);
    const isCorrect = selectedOption?.isCorrect;

    return (
      <Box>
        {slide.questionContent ? <EditorHTML content={slide.questionContent} /> : null}
        <SingleChoiceAnswer
          value={selectedId}
          spacing="3"
          onChange={(value: string) => setAnswer(slide.id, { selectedOptionId: value })}
        >
          {slide.options.map((option, index) => (
            <SingleChoiceAnswerCard
              key={option.id}
              value={option.id}
              isCorrect={selectedId ? isCorrect : undefined}
              selectedOptions={selectedId ? [selectedId] : []}
              readOnly={false}
              index={index}
            >
              <Text color="fg.emphasis" textStyle="sm">
                {option.answer}
              </Text>
            </SingleChoiceAnswerCard>
          ))}
        </SingleChoiceAnswer>
      </Box>
    );
  }

  if (slide.kind === SlideKind.OPEN_QUESTION) {
    return (
      <Textarea
        placeholder={t`Type your answer here`}
        onChange={(e) => setAnswer(slide.id, { textInput: e.target.value })}
        mt="16px"
        value={answer?.textInput ?? ''}
      />
    );
  }

  if (slide.kind === SlideKind.FILE_UPLOAD_PROMPT) {
    const recordingKinds =
      isMediaRecordingReleased && isMediaRecordingSupported() ? (slide.recordingKinds ?? []) : [];
    return (
      <VStack spacing="4" alignItems="flex-start" mt="16px">
        <Text textStyle="md" fontWeight="600" color="fg.default">
          {recordingKinds.length > 0 ? (
            <Trans>Record or upload your response</Trans>
          ) : (
            <Trans>File upload</Trans>
          )}
        </Text>
        <FileUploadRoot maxFiles={PREVIEW_MAX_FILES} maxSizePerFile={MAX_FILE_UPLOAD_SIZE_MB}>
          <FileUploadList />
          {recordingKinds.length > 0 ? (
            <PreviewRecordResponse recordingKinds={recordingKinds} />
          ) : null}
          <FileUploadDropzone
            fileTypeLabels={['Image', 'Video', 'Audio', 'PDF', 'Word', 'Excel', 'PowerPoint']}
          >
            <VStack spacing="3">
              <IconButton aria-label={t`Upload`} icon={<Upload size="24" />} variant="ghost" />
              <VStack spacing="1">
                <HStack spacing="1" textStyle="md" color="fg.muted" whiteSpace="nowrap">
                  <Text fontWeight="600">
                    <Trans>Click to upload</Trans>
                  </Text>
                  <Text>
                    <Trans>or drag and drop</Trans>
                  </Text>
                </HStack>
                <Text textStyle="xs" color="fg.muted">
                  <Trans>
                    You can add up to {PREVIEW_MAX_FILES} files (PDF, Word, Excel, PowerPoint,
                    image, audio, or video), up to {maxFileSizeGb}GB each.
                  </Trans>
                </Text>
              </VStack>
            </VStack>
          </FileUploadDropzone>
        </FileUploadRoot>
        <Box w="full">
          <Text fontSize="16px" color="fg.default" mb={4}>
            <Trans>Supporting text</Trans>
          </Text>
          <Textarea
            placeholder={t`Type your answer here`}
            onChange={(e) => setAnswer(slide.id, { textInput: e.target.value })}
            value={answer?.textInput ?? ''}
          />
        </Box>
      </VStack>
    );
  }

  if (slide.kind === SlideKind.FORM && slide.sections) {
    return <PreviewFormSlide slide={slide} answer={answer} setAnswer={setAnswer} />;
  }

  return null;
}

function AssessmentSlidesPreview({ slides }: AssessmentSlidesPreviewProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, LocalAnswer>>({});

  const flatSlides = useMemo(() => flattenSlides(slides), [slides]);
  const totalSlides = flatSlides.length;
  const currentSlide = flatSlides[currentIndex];

  const progress = totalSlides > 0 ? Math.ceil((currentIndex / totalSlides) * 100) : 0;

  const onNext = useCallback(() => {
    setCurrentIndex((i) => (i < totalSlides - 1 ? i + 1 : i));
  }, [totalSlides]);

  const onPrevious = useCallback(() => {
    setCurrentIndex((i) => (i > 0 ? i - 1 : i));
  }, []);

  const setAnswer = useCallback((questionId: string, answer: Partial<LocalAnswer>) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], ...answer },
    }));
  }, []);

  if (!currentSlide) {
    return null;
  }

  return (
    <Box>
      <Progress value={progress} mb={6} h={2} hasStripe />
      <Text fontWeight="600" fontSize="24px" mb={4}>
        {currentSlide.title}
      </Text>

      <PreviewSlideContent slide={currentSlide} />
      <PreviewSlideQuestion
        slide={currentSlide}
        answer={answers[currentSlide.id]}
        setAnswer={setAnswer}
      />

      <HStack justify="space-between" mt="6">
        <Button
          variant="solid"
          onClick={onPrevious}
          leftIcon={<ChevronLeft />}
          isDisabled={currentIndex === 0}
        >
          <Trans>Previous</Trans>
        </Button>
        <Text color="fg.muted" fontSize="sm">
          {currentIndex + 1} / {totalSlides}
        </Text>
        <Button
          variant="solid"
          onClick={onNext}
          rightIcon={<ChevronRight />}
          isDisabled={currentIndex === totalSlides - 1}
        >
          <Trans>Next</Trans>
        </Button>
      </HStack>
    </Box>
  );
}

export function AssessmentPreview({
  unitId,
  lastPublishedVersionId,
  version,
  versionToggle,
}: AssessmentPreviewProps) {
  const assessmentQuery = trpcAdmin.unitAssessment.byUnitId.useQuery(unitId);

  const draftVersionId = assessmentQuery.data?.draftVersion?.id ?? null;
  const versionId =
    version === 'published' && lastPublishedVersionId !== null
      ? lastPublishedVersionId
      : draftVersionId;

  const slidesQuery = trpcAdmin.unitAssessment.getSlides.useQuery(
    { unitAssessmentVersionId: versionId ?? '' },
    { enabled: versionId !== null },
  );

  const slides = slidesQuery.data?.slides;
  const isLoading = assessmentQuery.isLoading || slidesQuery.isLoading;

  return (
    <Box>
      {versionToggle ? <Flex mb="4">{versionToggle}</Flex> : null}
      {isLoading ? (
        <Flex justify="center" align="center" minH="200px">
          <Spinner />
        </Flex>
      ) : slides?.length ? (
        <FileUrlsProvider fileUrls={slidesQuery.data?.fileUrls}>
          <AssessmentSlidesPreview slides={slides} />
        </FileUrlsProvider>
      ) : (
        <Box textAlign="center" py="8">
          <Text color="fg.muted">
            <Trans>No slides available for this version.</Trans>
          </Text>
        </Box>
      )}
    </Box>
  );
}
